import { Link, useNavigate } from 'react-router-dom';
import { PiCaretDownBold, PiCaretUpBold, PiSnowflakeBold } from 'react-icons/pi';
import { Checkbox, Skeleton } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import { cn } from '@v2/utils/cn';
import type { PloomesDealRow } from './types';
import { ploomesDealPath } from './dealPath';
import { contractBadgeClass, formatBrl, formatDateTime, listBadgeClass, shortDisplayName, statusBadgeColor } from './utils';
import StockBreakdownBadges from './StockBreakdownBadges';
import SupplierAvatars from './SupplierAvatars';
import DealOwnerCell from './DealOwnerCell';
import PaymentMethodBadge from './PaymentMethodBadge';
import { useI18n } from '@v2/providers/I18nProvider';

/** Colunas ordenáveis no backend (`PloomesDealListService`). */
type SortColumn =
    | 'internal_number'
    | 'title'
    | 'contact_name'
    | 'contact_state'
    | 'contract_type'
    | 'status_name'
    | 'amount'
    | 'owner_name'
    | 'finish_date';

const DEFAULT_DESC_COLUMNS = new Set<SortColumn>(['amount', 'finish_date', 'internal_number']);

function parseSort(sort: string | undefined | null): { column: string; direction: 'asc' | 'desc' } {
    const value = sort && typeof sort === 'string' ? sort : '-finish_date';
    if (value.startsWith('-')) {
        return { column: value.slice(1), direction: 'desc' };
    }
    return { column: value, direction: 'asc' };
}

function nextSortValue(currentSort: string | undefined | null, column: SortColumn): string {
    const { column: active, direction } = parseSort(currentSort);
    if (active === column) {
        return direction === 'asc' ? `-${column}` : column;
    }
    return DEFAULT_DESC_COLUMNS.has(column) ? `-${column}` : column;
}

function SortableTh({
    label,
    column,
    sort,
    onSort,
    className,
}: {
    label: string;
    column: SortColumn;
    sort?: string | null;
    onSort: (sort: string) => void;
    className?: string;
}) {
    const { column: active, direction } = parseSort(sort);
    const isActive = active === column;

    return (
        <th className={cn('px-3 py-3 text-left', className)}>
            <button
                type="button"
                onClick={() => onSort(nextSortValue(sort, column))}
                className={cn(
                    'inline-flex items-center gap-1 rounded-md px-1 py-0.5 transition-colors hover:bg-gray-200/70 hover:text-gray-900 dark:hover:bg-zinc-700 dark:hover:text-white',
                    isActive ? 'text-orange-700 dark:text-orange-400' : 'text-gray-500',
                )}
                title={isActive ? (direction === 'asc' ? 'Ordenar descendente' : 'Ordenar ascendente') : 'Ordenar coluna'}
            >
                <span>{label}</span>
                <span className="flex flex-col leading-none">
                    <PiCaretUpBold
                        className={cn(
                            'h-2.5 w-2.5',
                            isActive && direction === 'asc' ? 'text-orange-600' : 'text-gray-300 dark:text-zinc-600',
                        )}
                    />
                    <PiCaretDownBold
                        className={cn(
                            '-mt-0.5 h-2.5 w-2.5',
                            isActive && direction === 'desc' ? 'text-orange-600' : 'text-gray-300 dark:text-zinc-600',
                        )}
                    />
                </span>
            </button>
        </th>
    );
}

function rowClassName(row: PloomesDealRow): string {
    if (row.has_defaulted_installment) {
        return 'cursor-pointer border-l-4 border-l-rose-500 bg-rose-50/80 hover:bg-rose-100/80 dark:border-l-rose-500 dark:bg-rose-950/25 dark:hover:bg-rose-950/40';
    }

    if (row.commission_frozen_legacy) {
        return 'cursor-pointer border-l-4 border-l-sky-400 bg-sky-50/80 hover:bg-sky-100/80 dark:border-l-sky-500 dark:bg-sky-950/30 dark:hover:bg-sky-950/45';
    }

    if (row.is_buckler_one_client) {
        return 'cursor-pointer border-l-4 border-l-amber-400 bg-amber-50/70 hover:bg-amber-100/80 dark:border-l-amber-500 dark:bg-amber-950/25 dark:hover:bg-amber-950/40';
    }

    return 'cursor-pointer hover:bg-gray-50/80 dark:hover:bg-zinc-800/50';
}

function rowTitle(row: PloomesDealRow, t: (key: string, params?: Record<string, string | number>) => string): string | undefined {
    if (row.has_defaulted_installment) {
        return undefined;
    }
    if (row.commission_frozen_legacy) {
        return t('v2.ploomes_deals.badges.commission_frozen_hint');
    }
    if (row.is_buckler_one_client) {
        return `Rede Buckler One — ${row.buckler_one_franchisor_name ?? 'franqueadora'}`;
    }
    return undefined;
}

function TableSkeletonRows({ count = 8 }: { count?: number }) {
    return (
        <>
            {Array.from({ length: count }, (_, i) => (
                <tr key={i}>
                    <td className="px-3 py-3"><Skeleton className="h-4 w-4 rounded" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-4 w-40" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-7 w-16 rounded-full" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-5 w-20 rounded-full" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-4 w-28" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-5 w-8 rounded-full" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-3 py-3">
                        <div className="space-y-1">
                            <Skeleton className="h-5 w-24 rounded-full" />
                            <Skeleton className="h-5 w-24 rounded-full" />
                        </div>
                    </td>
                    <td className="px-3 py-3"><Skeleton className="h-5 w-20 rounded-full" /></td>
                    <td className="px-3 py-3"><Skeleton className="h-4 w-20" /></td>
                    <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-7 w-7 rounded-full" />
                            <Skeleton className="h-4 w-24" />
                        </div>
                    </td>
                    <td className="px-3 py-3"><Skeleton className="h-4 w-24" /></td>
                </tr>
            ))}
        </>
    );
}

type Props = {
    rows: PloomesDealRow[];
    loading: boolean;
    page: number;
    perPage: number;
    lastPage: number;
    total: number;
    sort?: string;
    onSortChange?: (sort: string) => void;
    onPageChange: (page: number) => void;
    onPerPageChange: (perPage: number) => void;
    selectedIds: Set<number>;
    onToggleRow: (id: number) => void;
    onToggleAll: (ids: number[]) => void;
};

export default function PloomesDealListTable({
    rows,
    loading,
    page,
    perPage,
    lastPage,
    total,
    sort = '-finish_date',
    onSortChange,
    onPageChange,
    onPerPageChange,
    selectedIds,
    onToggleRow,
    onToggleAll,
}: Props) {
    const navigate = useNavigate();
    const { t } = useI18n();
    const rowIds = rows.map((r) => r.id);
    const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));
    const handleSort = onSortChange ?? (() => undefined);

    return (
        <div>
            <p className="mb-3 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50/80 px-2 py-1 dark:border-amber-800 dark:bg-amber-950/30">
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-black text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/20 dark:text-amber-400">
                        <span className="size-1.5 rounded-full bg-amber-500 dark:bg-amber-400" aria-hidden />
                        Buckler One
                    </span>
                    <span>— deal ganho a partir do início do contrato da franqueadora</span>
                </span>
            </p>
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
            <div className="ds-table-container overflow-x-auto border-0">
                <table className="min-w-full text-sm">
                    <thead className="border-b border-gray-200 bg-gray-50/80 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-zinc-700 dark:bg-zinc-800/80 dark:text-zinc-400">
                        <tr>
                            <th className="w-10 px-3 py-3">
                                <Checkbox
                                    checked={allSelected}
                                    onChange={() => onToggleAll(rowIds)}
                                    aria-label="Selecionar todos"
                                    disabled={loading}
                                />
                            </th>
                            <SortableTh label="Nº Pedido" column="internal_number" sort={sort} onSort={handleSort} />
                            <SortableTh label="Título" column="title" sort={sort} onSort={handleSort} />
                            <th className="px-3 py-3 text-left">Fornecedores</th>
                            <th className="px-3 py-3 text-left">Estoque</th>
                            <SortableTh label="Cliente" column="contact_name" sort={sort} onSort={handleSort} />
                            <SortableTh label="UF" column="contact_state" sort={sort} onSort={handleSort} />
                            <SortableTh label="Contrato" column="contract_type" sort={sort} onSort={handleSort} />
                            <th className="px-3 py-3 text-left">{t('v2.ploomes_deals.col_payment_methods')}</th>
                            <SortableTh label="Status" column="status_name" sort={sort} onSort={handleSort} />
                            <SortableTh label="Valor" column="amount" sort={sort} onSort={handleSort} />
                            <SortableTh label="Responsável" column="owner_name" sort={sort} onSort={handleSort} />
                            <SortableTh label="Ganho em" column="finish_date" sort={sort} onSort={handleSort} />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading && <TableSkeletonRows count={8} />}
                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={13} className="px-4 py-10 text-center text-gray-500">
                                    Nenhuma proposta encontrada.
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            rows.map((row) => {
                                const tradeName = shortDisplayName(row.contact_name);
                                const legalName = shortDisplayName(row.contact_legal_name);
                                const clientName = tradeName.short ? tradeName : legalName;
                                const secondaryName =
                                    tradeName.short &&
                                    legalName.short &&
                                    tradeName.full.trim().toLowerCase() !== legalName.full.trim().toLowerCase()
                                        ? legalName
                                        : null;

                                return (
                                <tr
                                    key={row.id}
                                    className={rowClassName(row)}
                                    title={rowTitle(row, t)}
                                    onClick={() => navigate(ploomesDealPath(row))}
                                >
                                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={selectedIds.has(row.id)}
                                            onChange={() => onToggleRow(row.id)}
                                            aria-label={`Selecionar ${row.title}`}
                                        />
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-3">
                                        {(() => {
                                            const nums =
                                                row.sale_numbers && row.sale_numbers.length > 0
                                                    ? row.sale_numbers.map(String)
                                                    : row.ploomes_order_numbers && row.ploomes_order_numbers.length > 0
                                                      ? row.ploomes_order_numbers.map(String)
                                                      : [
                                                            String(
                                                                row.display_sale_number ||
                                                                    (Number(row.amount) > 0
                                                                        ? row.internal_number ||
                                                                          row.deal_number ||
                                                                          `#${row.id}`
                                                                        : row.deal_number || `#${row.id}`),
                                                            ),
                                                        ];
                                            if (nums.length > 1) {
                                                return (
                                                    <div className="flex flex-wrap gap-1">
                                                        {nums.map((n) => (
                                                            <span key={n} className={listBadgeClass('primary')}>
                                                                {n}
                                                            </span>
                                                        ))}
                                                    </div>
                                                );
                                            }
                                            return <span className={listBadgeClass('primary')}>{nums[0]}</span>;
                                        })()}
                                    </td>
                                    <td className="max-w-[200px] px-3 py-3">
                                        <Link
                                            to={ploomesDealPath(row)}
                                            className="line-clamp-2 font-medium text-gray-900 hover:text-primary hover:underline dark:text-zinc-100 dark:hover:text-amber-300"
                                            title={row.title}
                                        >
                                            {row.title}
                                        </Link>
                                    </td>
                                    <td className="px-3 py-3">
                                        <SupplierAvatars suppliers={row.suppliers} />
                                    </td>
                                    <td className="px-3 py-3">
                                        <StockBreakdownBadges breakdown={row.stock_breakdown} />
                                    </td>
                                    <td className="px-3 py-3">
                                        <div className="flex flex-col items-start gap-1">
                                            {clientName.short ? (
                                                <span
                                                    className={cn(listBadgeClass('secondary'), 'max-w-[160px] truncate')}
                                                    title={clientName.full}
                                                >
                                                    {clientName.short}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
                                            )}
                                            {secondaryName && (
                                                <span
                                                    className="max-w-[160px] truncate text-[10px] text-gray-500 dark:text-zinc-400"
                                                    title={`${t('v2.ploomes_deals.client_tab.legal_name')}: ${secondaryName.full}`}
                                                >
                                                    {secondaryName.short}
                                                </span>
                                            )}
                                            {row.is_buckler_one_client && (
                                                <span className="inline-flex w-fit items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-black text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/20 dark:text-amber-400">
                                                    <span className="size-1.5 rounded-full bg-amber-500 dark:bg-amber-400" aria-hidden />
                                                    Buckler One
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-3">
                                        {row.contact_state ? (
                                            <span className={cn(listBadgeClass('amber'), 'font-bold tracking-wide')}>
                                                {row.contact_state}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-3">
                                        {row.contract_type_label ? (
                                            <span
                                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${contractBadgeClass(row.contract_type_label)}`}
                                            >
                                                {row.contract_type_label}
                                            </span>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex min-w-[9rem] flex-col gap-1">
                                            <div className="flex items-center gap-1.5">
                                                <span className="w-8 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                    {t('v2.ploomes_deals.payment_entry_short')}
                                                </span>
                                                <PaymentMethodBadge
                                                    label={row.entry_payment_method}
                                                    kind={row.entry_payment_method_kind}
                                                    size="sm"
                                                />
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="w-8 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                                    {t('v2.ploomes_deals.payment_remaining_short')}
                                                </span>
                                                <PaymentMethodBadge
                                                    label={row.remaining_payment_method}
                                                    kind={row.remaining_payment_method_kind}
                                                    size="sm"
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3">
                                        <div className="flex flex-col gap-1">
                                            {row.status_name ? (
                                                <span className={listBadgeClass(statusBadgeColor(row.status_badge_color))}>
                                                    {row.status_name}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
                                            )}
                                            {row.has_defaulted_installment && (
                                                <span className={listBadgeClass('danger')}>
                                                    Inadimplente
                                                </span>
                                            )}
                                            {row.commission_frozen_legacy && (
                                                <span
                                                    className="inline-flex w-fit items-center gap-1 rounded-full border border-sky-300 bg-sky-100 px-2 py-0.5 text-[10px] font-black text-sky-800 dark:border-sky-500/40 dark:bg-sky-500/20 dark:text-sky-300"
                                                    title={t('v2.ploomes_deals.badges.commission_frozen_hint')}
                                                >
                                                    <PiSnowflakeBold className="size-3 shrink-0" aria-hidden />
                                                    {t('v2.ploomes_deals.badges.commission_frozen_list')}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-3">
                                        {row.amount != null && Number(row.amount) > 0 ? (
                                            <span className={cn(listBadgeClass('emerald'), 'px-3 py-1.5 text-sm font-bold tabular-nums')}>
                                                {formatBrl(row.amount)}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
                                        )}
                                    </td>
                                    <td className="max-w-[160px] px-3 py-3">
                                        <DealOwnerCell name={row.owner_name} avatarUrl={row.owner_avatar_url} compactName />
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-3">
                                        {row.finish_date ? (
                                            <span className={cn(listBadgeClass('blue'), 'tabular-nums')}>
                                                {formatDateTime(row.finish_date)}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-400 dark:text-zinc-500">—</span>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                    </tbody>
                </table>
            </div>

            <TablePagination
                meta={{
                    current_page: page,
                    last_page: lastPage,
                    per_page: perPage,
                    total,
                }}
                onPageChange={onPageChange}
                onPerPageChange={onPerPageChange}
                perPageOptions={[...TABLE_PER_PAGE_OPTIONS]}
                itemLabel="proposta"
            />
            </div>
        </div>
    );
}
